/**
 * Which tool calls are checkpoint events, and what they say.
 *
 * Saving and restoring a checkpoint are the two moments where the agent moves
 * the project as a whole, so they read as markers across the transcript rather
 * than as tool rows folded into a run. Both hosts name these tools the same
 * (`checkpoint`, `restore_checkpoint`), so the rule lives here once and applies
 * to every surface on the shell.
 *
 * Only a SETTLED call is a marker: a checkpoint that is still running, or that
 * failed, is ordinary tool activity and renders like any other row.
 */
import { toolDisplayName } from "./ToolCallView";
import type { AnyToolPart } from "./types";

export interface CheckpointEvent {
  kind: "save" | "restore";
  /** What the marker says. Always a whole phrase, never assembled from parts. */
  label: string;
}

/** The label the agent gave the checkpoint, when it supplied one. */
function describedAs(input: unknown): string | null {
  if (typeof input !== "object" || input === null) return null;
  const value = (input as { description?: unknown }).description;
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function checkpointEvent(part: AnyToolPart): CheckpointEvent | null {
  const name = toolDisplayName(part);
  if (name !== "checkpoint" && name !== "restore_checkpoint") return null;
  if (part.state !== "output-available") return null;
  const described = describedAs(part.input);
  if (name === "checkpoint") {
    return {
      kind: "save",
      label: described ?? "Checkpoint saved",
    };
  }
  return {
    kind: "restore",
    label: described
      ? `Restored to “${described}”`
      : "Restored an earlier checkpoint",
  };
}

/** True for any checkpoint tool call, settled or not: the grouping rule needs
 *  to keep a marker out of a fold before its output arrives. */
export function isCheckpointTool(part: AnyToolPart): boolean {
  const name = toolDisplayName(part);
  return name === "checkpoint" || name === "restore_checkpoint";
}
